import React from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@mui/material'
import { BaseRouteParams, useNavigation } from 'hocs'
import { DateRange, dateRangeFromQuery, dateRangeToQuery } from 'dateRanges'
import DateRangePicker from 'uiComponents/popups/comparisonDateRangePicker'
import { SingleSelect } from 'uiComponents/input'
import { PageTitle } from 'uiComponents/typography'
import { PlainLoader } from 'uiComponents/loaders'
import { LucideIcon } from 'utils/lucideIcon'
import { useListAuditLogActorsQuery, useListAuditLogEntriesQuery } from './reduxQuery'
import { OBJECT_TYPE_LABELS, SOURCE_LABELS, STAFF_MASK } from './schema'
import { EntryRow } from './entryRow'
import './styles.scss'

const cursorFromLink = (link?: string | null) => (link ? new URL(link).searchParams.get('cursor') : null)

const AuditLogPage = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const navigation = useNavigation()
    const query = navigation.query()
    const dateRange = dateRangeFromQuery(query, 'last30days')
    const filtersActive = !!(query.objectType || query.actor || query.source || query.objectId)

    const entries = useListAuditLogEntriesQuery(
        {
            accountSlug,
            from: dateRange.from.toISOString(),
            to: dateRange.to.toISOString(),
            objectType: query.objectType,
            objectId: query.objectId,
            actor: query.actor,
            source: query.source,
            cursor: query.cursor,
            pageSize: 25,
        },
        { refetchOnMountOrArgChange: true },
    )
    const actors = useListAuditLogActorsQuery(accountSlug, { refetchOnMountOrArgChange: true })

    const setFilter = (q: Record<string, string | null>) => navigation.addQueryWithReplace({ ...q, cursor: null })
    const onDateRangeChanged = (range: DateRange) => setFilter(dateRangeToQuery(range))
    const selectOptions = (labels: Record<string, string>) =>
        Object.entries(labels).map(([value, name]) => ({ value, name }))
    const actorOptions = (actors.data ?? []).map((a) => ({ value: a, name: a === STAFF_MASK ? 'Convious staff' : a }))

    const results = entries.data?.results ?? []
    const previous = cursorFromLink(entries.data?.previous)
    const next = cursorFromLink(entries.data?.next)

    return (
        <div className="audit-log-page">
            <PageTitle>Activity log</PageTitle>
            <p className="audit-log-subtitle">
                Who changed your venue&apos;s settings, and what changed. Times shown in your browser timezone.
            </p>

            <div className="audit-log-filters">
                <SingleSelect
                    options={selectOptions(OBJECT_TYPE_LABELS)}
                    noSelectOption="All types"
                    selected={query.objectType ?? null}
                    onSelect={(v) => setFilter({ objectType: v || null })}
                    width="18em"
                    whiteBackground
                    withClear
                />
                <SingleSelect
                    options={actorOptions}
                    noSelectOption="Anyone"
                    selected={query.actor ?? null}
                    onSelect={(v) => setFilter({ actor: v || null })}
                    width="20em"
                    whiteBackground
                    withSearch
                    withClear
                />
                <SingleSelect
                    options={selectOptions(SOURCE_LABELS)}
                    noSelectOption="Made anywhere"
                    selected={query.source ?? null}
                    onSelect={(v) => setFilter({ source: v || null })}
                    width="14em"
                    whiteBackground
                    withClear
                />
                {filtersActive && (
                    <button
                        type="button"
                        className="audit-clear-filters"
                        onClick={() => setFilter({ objectType: null, actor: null, source: null, objectId: null })}
                    >
                        Clear filters
                    </button>
                )}
                <div className="audit-log-date">
                    <DateRangePicker range={dateRange} onChange={onDateRangeChanged} allowFutureDateSelection />
                </div>
                {query.objectId && (
                    <button type="button" className="audit-scope" onClick={() => setFilter({ objectId: null })}>
                        <LucideIcon iconName="Filter" size={13} />
                        One object&apos;s history
                        <LucideIcon iconName="X" size={13} />
                    </button>
                )}
            </div>

            <div className="audit-log-list">
                {entries.isError && (
                    <div className="audit-log-state">
                        <div className="audit-log-state-title">We couldn&apos;t load the activity log</div>
                        <Button size="medium" variant="outlined" onClick={() => entries.refetch()}>
                            Try again
                        </Button>
                    </div>
                )}
                {entries.isLoading && <PlainLoader style={{ width: '4rem', margin: '3rem auto', display: 'block' }} />}
                {entries.isSuccess && results.length === 0 && (
                    <div className="audit-log-state">
                        <LucideIcon iconName="Clock" size={36} className="audit-log-state-icon" />
                        <div className="audit-log-state-title">
                            {filtersActive ? 'No changes match this filter' : 'No changes recorded yet'}
                        </div>
                        {!filtersActive && <p>Changes to time slots and opening times will show up here.</p>}
                    </div>
                )}
                <div
                    className={entries.isFetching && !entries.isLoading ? 'audit-log-rows fetching' : 'audit-log-rows'}
                >
                    {results.map((entry) => (
                        <EntryRow key={entry.id} entry={entry} onSelectObject={(objectId) => setFilter({ objectId })} />
                    ))}
                </div>
            </div>

            {(previous || next) && (
                <div className="audit-log-pagination">
                    <Button
                        size="medium"
                        disabled={!previous}
                        onClick={() => navigation.addQueryWithReplace({ cursor: previous })}
                    >
                        Newer
                    </Button>
                    <Button
                        size="medium"
                        disabled={!next}
                        onClick={() => navigation.addQueryWithReplace({ cursor: next })}
                    >
                        Older
                    </Button>
                </div>
            )}
        </div>
    )
}

export default AuditLogPage
